import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';

const sections = [
  {
    number: '1',
    title: 'The Service; Two User Types',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>The Service supports:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <span className="font-medium text-foreground">Customers:</span> users who submit Case
            Materials and receive AI-generated preliminary reports and may hire Experts.
          </li>
          <li>
            <span className="font-medium text-foreground">Experts:</span> independent professionals
            who offer services through marketplace features.
          </li>
        </ul>
        <p>
          Grunt Logic operates the platform. Experts are independent contractors and are not
          employees, agents, or representatives of Grunt Logic.
        </p>
      </div>
    ),
  },
  {
    number: '2',
    title: 'Agreement Structure; Incorporation by Reference',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          These Terms incorporate by reference, and you agree to, the following (together with
          these Terms, the "Agreement"):
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>the Privacy Policy</li>
          <li>any posted Refund Policy (if applicable)</li>
          <li>any posted Marketplace rules and policies (if applicable)</li>
          <li>any Order Form or enterprise agreement you sign with us</li>
        </ul>
        <p>
          If there is a conflict, the following order controls: (1) enterprise Order Form, (2)
          these Terms, (3) policies/rules posted in the Service.
        </p>
      </div>
    ),
  },
  {
    number: '3',
    title: 'Eligibility; Accounts; Security',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          You must be at least 18 and capable of forming a contract. You agree to provide accurate
          account information and keep credentials secure. We may require or enforce 2FA and other
          security controls.
        </p>
      </div>
    ),
  },
  {
    number: '4',
    title: 'Electronic Signatures; E-Sign Consent',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          You agree that clicking an "I agree" checkbox/button, creating an account, purchasing,
          or otherwise indicating assent electronically constitutes your electronic signature and
          acceptance of the Agreement. You agree we may provide notices and disclosures
          electronically (e.g., by email, in-app notification, or posting in the Service).
        </p>
      </div>
    ),
  },
  {
    number: '5',
    title: 'Critical Disclaimers: Not Advice; Not an Expert Report; Not for Emergency Use',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          FireCaseIQ provides AI-generated preliminary assessments. The Service and any reports:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>are informational only</li>
          <li>are not legal advice</li>
          <li>are not engineering opinions</li>
          <li>are not a licensed on-site fire investigation</li>
          <li>are not insurance coverage determinations</li>
          <li>are not affidavits or retained expert reports</li>
          <li>
            do not create an attorney-client relationship, expert-client relationship,
            insurer-insured relationship, or fiduciary duty
          </li>
        </ul>
        <p className="font-medium text-foreground">
          Not for emergency response / life safety decisions.
        </p>
        <p>
          Do not use the Service for emergency response, imminent danger situations, or real-time
          life safety decisions. Contact emergency services for emergencies.
        </p>
        <p>
          You are responsible for verifying outputs and consulting qualified licensed professionals
          before making decisions.
        </p>
      </div>
    ),
  },
  {
    number: '6',
    title: 'Technology Used (Transparency)',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>To operate the Service, we use third-party providers such as:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <span className="font-medium text-foreground">OpenAI (ChatGPT API)</span> for AI
            analysis and report drafting
          </li>
          <li>
            <span className="font-medium text-foreground">Supabase</span> for authentication,
            database, and storage
          </li>
          <li>
            <span className="font-medium text-foreground">Stripe</span> for
            payments/subscriptions and related billing features
          </li>
          <li>
            <span className="font-medium text-foreground">Resend</span> for transactional emails
          </li>
          <li>
            <span className="font-medium text-foreground">Vercel</span> for hosting/deployment
            and operational infrastructure
          </li>
        </ul>
        <p>
          Additional providers may be used for analytics, error monitoring, anti-abuse, support
          tooling, and performance monitoring.
        </p>
      </div>
    ),
  },
  {
    number: '7',
    title: 'User Content; License',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          You retain ownership of content you submit ("User Content"), including Case Materials
          and Expert Content, subject to the license below.
        </p>
        <p className="font-medium text-foreground">License to Operate the Service.</p>
        <p>
          You grant Grunt Logic a worldwide, non-exclusive, royalty-free license to host, store,
          process, transmit, and display User Content solely to provide and maintain the Service,
          including generating reports, annotations, facilitating Expert engagements, preventing
          abuse, and enforcing these Terms.
        </p>
        <p>
          You represent you have the rights to submit User Content and that doing so does not
          violate law or third-party rights.
        </p>
      </div>
    ),
  },
  {
    number: '8',
    title: 'Acceptable Use',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            submit unlawful, infringing, or unauthorized content (including third-party
            confidential materials without permission)
          </li>
          <li>submit Sensitive Data unless strictly necessary; redact where possible</li>
          <li>attempt to hack, scrape, reverse engineer, or disrupt the Service</li>
          <li>bypass access controls, probe vulnerabilities, or misuse accounts</li>
          <li>use the Service to harass, defame, or threaten others</li>
          <li>use the Service for any prohibited or illegal purpose</li>
        </ul>
        <p>We may suspend or terminate accounts for violations.</p>
      </div>
    ),
  },
  {
    number: '9',
    title: 'Standards References (No Compliance Claims)',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          Reports may reference frameworks or standards (e.g., NFPA 921, NFPA 1033, ASTM E2713,
          NEC, NEMA guidance, IEEE/UL guidance) to structure output. References do not mean
          certification, formal compliance, or admissibility.
        </p>
      </div>
    ),
  },
  {
    number: '10',
    title: 'Plans, Credits, Trial, Billing, and "Submit Once" Rule',
    content: (
      <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
        <div>
          <p className="font-medium text-foreground mb-1">A. Plans and Credits</p>
          <p>
            Access may be via one-time credits, subscriptions, or enterprise agreements. Plan
            details are shown at checkout or in an enterprise order form.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">B. Free First Report / Trial</p>
          <p>
            A free first report may be offered only with: verified email, and valid payment method
            on file.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">
            C. One-shot Intake; Revisions Count as New Cases
          </p>
          <p>
            Customers submit Case Materials "all at once." Any revision, additional materials, or
            re-run requested after submission counts as a new case and consumes an additional
            credit.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">D. Payments</p>
          <p>
            Payments are processed by Stripe (or another processor). You authorize charges for
            your plan and applicable taxes.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">E. Refunds</p>
          <p>
            Except where required by law or stated in a posted Refund Policy, credits are
            generally non-refundable once a report is generated. We may issue refunds for
            duplicate charges or technical failure where no report is produced. Marketplace escrow
            disputes are handled as described below.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">F. Enterprise</p>
          <p>
            Enterprise inquiries should be directed to{' '}
            <a href="mailto:sales@firecaseiq.com" className="text-primary hover:underline">
              sales@firecaseiq.com
            </a>
            .
          </p>
        </div>
      </div>
    ),
  },
  {
    number: '11',
    title: 'Marketplace Terms (Customers)',
    content: (
      <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
        <div>
          <p className="font-medium text-foreground mb-1">A. Platform Role</p>
          <p>
            We provide a marketplace and platform tools. We do not guarantee Expert availability,
            performance, outcomes, or licensure validity (though we may vet/approve Experts for
            platform participation).
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">B. Contracts and Communications</p>
          <p>When you hire an Expert:</p>
          <ul className="list-disc pl-5 space-y-1 mt-1">
            <li>you may be required to authorize escrow or a payment hold</li>
            <li>
              communication may be limited until acceptance/payment steps are completed
            </li>
            <li>you and the Expert are responsible for defining scope and deliverables</li>
          </ul>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">C. No Off-Platform Circumvention</p>
          <p>
            You agree not to circumvent the platform by paying off-platform, soliciting
            off-platform work from leads introduced through the Service, or sharing
            contact/payment info to bypass platform rules. We may monitor messages for
            circumvention patterns and enforce penalties (including suspension and withholding of
            funds during investigation).
          </p>
        </div>
      </div>
    ),
  },
  {
    number: '12',
    title: 'Marketplace Terms (Experts)',
    content: (
      <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
        <div>
          <p className="font-medium text-foreground mb-1">A. Independent Contractor Status</p>
          <p>
            Experts are independent contractors responsible for their own taxes, insurance,
            licensure, and professional conduct.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">
            B. Vetting/Approval; Credential Uploads
          </p>
          <p>
            Expert accounts may require credential uploads and admin approval. We may reject,
            suspend, or remove Experts at our discretion.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">C. Platform Fee / Commission</p>
          <p>
            We charge a platform fee deducted from Expert payouts (default 10%, adjustable by
            admin per Expert profile or per contract).
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">
            D. Confidentiality / NDA; Non-Solicitation
          </p>
          <p>
            Experts must keep Case Materials confidential, use them only for the engagement, and
            must not solicit Customers to move engagements off-platform for work arising from the
            Service. Violations may result in suspension, withholding of payouts, and other
            remedies.
          </p>
        </div>
      </div>
    ),
  },
  {
    number: '13',
    title: 'Payments, Escrow, Payouts, and Tax Reporting (Marketplace)',
    content: (
      <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
        <div>
          <p className="font-medium text-foreground mb-1">A. Stripe Payments and Payouts</p>
          <p>
            Customer payments and Expert payouts are processed through Stripe. Experts may be
            required to complete Stripe onboarding (including identity and tax-related
            information) to receive payouts.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">
            B. Limited Payment Collection Agent (Marketplace)
          </p>
          <p>
            For marketplace engagements, to the extent permitted by law, you authorize Grunt
            Logic to act as a limited payment collection agent for Experts solely for the purpose
            of accepting Customer payments through Stripe and remitting amounts due to Experts
            (less platform fees and adjustments permitted under this Agreement).
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">C. 1099-K</p>
          <p>
            Where required by applicable law and where reporting thresholds are met, Stripe will
            file and deliver Form 1099-K for marketplace payouts processed through Stripe. Experts
            are responsible for their own tax obligations.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">
            D. Holds, Adjustments, and Chargebacks
          </p>
          <p>
            We may place or maintain holds, delay payouts, reverse or adjust amounts, and/or
            offset amounts against future payouts to address disputes, chargebacks, suspected
            fraud, policy violations (including circumvention), compliance or onboarding issues,
            or negative balances.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">
            E. Stripe Connected Account Agreement
          </p>
          <p>
            Payment processing and payout services for Experts on FireCaseIQ are provided by
            Stripe and are subject to the Stripe Connected Account Agreement, which includes the
            Stripe Terms of Service (collectively, the "Stripe Services Agreement"). By agreeing
            to these Terms or continuing to operate as an Expert on the Service, you agree to be
            bound by the Stripe Services Agreement, as the same may be modified by Stripe from
            time to time. As a condition of Grunt Logic enabling payment processing services
            through Stripe, you agree to provide Grunt Logic accurate and complete information
            about you and your business, and you authorize Grunt Logic to share it and transaction
            information related to your use of the payment processing services provided by Stripe.
          </p>
        </div>
      </div>
    ),
  },
  {
    number: '14',
    title: 'Escrow and Disputes (Marketplace)',
    content: (
      <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
        <p>
          We may provide escrow holds, milestone approvals, time-entry approvals, and dispute
          tools. We may pause payouts during disputes and request information. Our role is limited
          to administering platform transactions and enforcing platform rules; we do not
          adjudicate technical truth or legal liability.
        </p>

        <div className="border-l-2 border-primary/30 pl-4 space-y-3">
          <p className="font-semibold text-foreground">
            14.1 Escrow Instructions (Release Only by Agreement or Arbitration/Court Outcome)
          </p>
          <p>If escrow/payment holds are used for an engagement:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Escrow funds will be held until release conditions are met.</li>
            <li>
              Escrow will be released only upon (i) joint written instructions from Customer and
              Expert (including in-app confirmations) or (ii) as required by a final arbitration
              award or court order.
            </li>
            <li>
              Grunt Logic does not decide fault, admissibility, negligence, or the technical
              merits of the dispute.
            </li>
          </ul>
        </div>

        <div className="border-l-2 border-primary/30 pl-4 space-y-4">
          <p className="font-semibold text-foreground">
            14.2 Customer ↔ Expert Dispute SOP (Email-Based)
          </p>
          <div>
            <p className="font-medium text-foreground mb-1">A. Open a Dispute (7 days)</p>
            <p>
              A Customer must open a dispute within 7 calendar days after the Expert marks a
              milestone/deliverable as submitted or delivered. To open a dispute, email{' '}
              <a href="mailto:disputes@firecaseiq.com" className="text-primary hover:underline">
                disputes@firecaseiq.com
              </a>{' '}
              with:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>Subject: "Marketplace Dispute — [Contract ID]"</li>
              <li>Customer account email and name</li>
              <li>Expert name or profile identifier</li>
              <li>Contract ID and milestone/time-entry reference</li>
              <li>Amount in dispute</li>
              <li>Brief description and requested resolution</li>
              <li>Supporting attachments (if any)</li>
            </ul>
            <p className="mt-2">
              If a dispute is not opened within the 7-day window, Customer may lose the ability
              to prevent release of escrow for that milestone.
            </p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">B. Informal Resolution (10 days)</p>
            <p>
              For 10 calendar days after a properly submitted dispute notice, Customer and Expert
              agree to attempt good-faith resolution using Service communications and written
              settlement instructions. Escrow remains frozen during this period.
            </p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">
              C. Arbitration Initiation Deadline (30 days)
            </p>
            <p>
              If the dispute is not resolved during the informal period, either party may initiate
              arbitration against the other party within 30 calendar days after the dispute notice
              was opened (the "Arbitration Initiation Deadline"). The initiating party must
              provide Grunt Logic reasonable proof of initiation (e.g., a filed demand and
              case/reference number).
            </p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">
              D. Default Rule (Customer Does Not Initiate Arbitration)
            </p>
            <p>
              If Customer does not initiate arbitration by the Arbitration Initiation Deadline,
              then (absent a court order) Customer instructs Grunt Logic to release the disputed
              escrow to the Expert.
            </p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">E. If Expert Initiates Arbitration</p>
            <p>
              If Expert initiates arbitration by the Arbitration Initiation Deadline and provides
              proof to Grunt Logic, escrow remains frozen pending joint written instructions or a
              final arbitration award/court order.
            </p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">F. Records and Cooperation</p>
            <p>
              You authorize Grunt Logic to retain and, if legally required, provide transaction
              records, acceptance logs, and relevant message history to an arbitrator or court.
            </p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">G. No Legal Advice; No Representation</p>
            <p>
              This SOP is an administrative process for holding and releasing escrow. Grunt Logic
              does not represent either party and does not provide legal advice.
            </p>
          </div>
        </div>
      </div>
    ),
  },
  {
    number: '15',
    title: 'Work Product; IP; Admissibility; Evidence Preservation (Marketplace)',
    content: (
      <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
        <div>
          <p className="font-medium text-foreground mb-1">A. Work Product Definition</p>
          <p>
            "Work Product" means deliverables created by an Expert specifically for a Customer
            under a marketplace engagement (e.g., written opinions, reports, annotations,
            summaries, or other deliverables).
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">
            B. Ownership / License Upon Payment (Default Rule)
          </p>
          <p>
            Unless the Customer and Expert expressly agree otherwise in writing within their
            engagement:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-1">
            <li>
              Upon full payment for the applicable milestone/engagement, the Expert assigns to
              the Customer all right, title, and interest in the Work Product excluding the
              Expert's pre-existing materials, templates, tools, general know-how, and
              methodologies ("Background IP").
            </li>
            <li>
              The Expert retains Background IP. To the extent Background IP is incorporated into
              Work Product, the Expert grants the Customer a perpetual, worldwide, royalty-free
              license to use the incorporated Background IP solely as embedded in the Work
              Product for the Customer's internal business, insurance, claims, and litigation
              uses.
            </li>
          </ul>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">
            C. No Platform Representation About Admissibility or Court Use
          </p>
          <p>
            Grunt Logic does not represent or warrant that any Work Product (or AI output) is
            admissible, "court-ready," or sufficient for any legal proceeding, insurance
            determination, or regulatory requirement. Admissibility and sufficiency vary by
            jurisdiction and forum.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">D. Expert Responsibility</p>
          <p>
            Experts are solely responsible for the substance of their Work Product, including any
            professional opinions, methodology, and any licensure or credential representations
            they choose to make.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">
            E. No Chain-of-Custody / Evidence Preservation
          </p>
          <p>
            The Service does not provide evidence preservation, chain-of-custody controls, or
            forensic integrity guarantees for uploaded materials. Customers and Experts are
            responsible for preserving original evidence and maintaining any chain-of-custody
            required for claims, litigation, or regulatory purposes.
          </p>
        </div>
      </div>
    ),
  },
  {
    number: '16',
    title: 'International Use (Customer and Expert Responsibility)',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          We are US-based (North Carolina). If you access the Service from outside the US, you
          are responsible for compliance with local laws applicable to your use (including
          privacy, export, professional licensure, consumer protection, and
          evidentiary/admissibility rules). You represent you have the right to submit any data
          you provide and to transfer it to the US.
        </p>
      </div>
    ),
  },
  {
    number: '17',
    title: 'AI Limitations; No Guarantees',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          AI outputs may be incorrect or incomplete. We do not guarantee accuracy, completeness,
          admissibility, case outcomes, insurance outcomes, litigation success, recovery amounts,
          or probability estimates.
        </p>
      </div>
    ),
  },
  {
    number: '18',
    title: 'Disclaimer of Warranties',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p className="uppercase font-medium text-foreground text-xs tracking-wide">
          The Service is provided "as is" and "as available." To the maximum extent permitted by
          law, we disclaim all warranties, including merchantability, fitness for a particular
          purpose, and non-infringement.
        </p>
      </div>
    ),
  },
  {
    number: '19',
    title: 'Limitation of Liability',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p className="uppercase font-medium text-foreground text-xs tracking-wide">
          To the maximum extent permitted by law: we are not liable for indirect, incidental,
          special, consequential, or punitive damages. Our total liability for all claims in any
          12-month period will not exceed the amount you paid us for the Service in that period.
        </p>
        <p>
          Some jurisdictions do not allow certain limitations; these Terms apply to the extent
          permitted.
        </p>
      </div>
    ),
  },
  {
    number: '20',
    title: 'Indemnification',
    content: (
      <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
        <div>
          <p className="font-medium text-foreground mb-1">A. General Indemnification</p>
          <p>
            You agree to indemnify and hold harmless Grunt Logic from claims arising out of your
            User Content, your violation of these Terms or law, or your interactions with other
            users/Experts.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">B. Legal Process and Subpoenas</p>
          <p>
            If Grunt Logic is required by subpoena, court order, or other legal process to
            produce documents, records, or personnel relating to your account, your User Content,
            or your marketplace engagements, and Grunt Logic is not a party to the underlying
            legal proceeding, you agree to reimburse Grunt Logic for our reasonable costs and
            expenses (including attorneys' fees, administrative time, and technical retrieval
            costs) incurred in responding to such legal process.
          </p>
        </div>
      </div>
    ),
  },
  {
    number: '21',
    title: 'Suspension / Termination',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          We may suspend or terminate access for violations, security risks, fraud,
          circumvention, or nonpayment. You may stop using the Service at any time.
        </p>
      </div>
    ),
  },
  {
    number: '22',
    title: 'Disputes Between You and Grunt Logic; Informal Resolution; Arbitration; Class Action Waiver (US Users)',
    content: (
      <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
        <p>
          This Section applies to disputes between you and Grunt Logic (not Customer–Expert
          disputes, which are handled under Section 14 for escrow administration).
        </p>
        <div>
          <p className="font-medium text-foreground mb-1">A. Informal Resolution First (30 days)</p>
          <p>
            Before starting arbitration, you agree to send a written Notice of Dispute to{' '}
            <a href="mailto:support@firecaseiq.com" className="text-primary hover:underline">
              support@firecaseiq.com
            </a>{' '}
            and allow 30 days to attempt resolution. The Notice must include your name, account
            email, a description of the dispute, and the relief requested.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">
            B. Binding Individual Arbitration (US Users)
          </p>
          <p>
            If you are a US resident and we cannot resolve the dispute informally, you and Grunt
            Logic agree to resolve the dispute by binding individual arbitration administered by
            the American Arbitration Association (AAA) under its then-current Consumer
            Arbitration Rules, unless AAA determines those rules do not apply, in which case
            AAA's closest applicable ruleset will apply. Arbitration will be conducted on an
            individual basis only.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">C. Location and Remote Proceedings</p>
          <p>
            Arbitration may occur in Wake County, North Carolina, by video/telephone, or in the
            county where you reside, as the arbitrator determines consistent with applicable
            rules and fairness considerations.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">D. Fees and Costs</p>
          <p>To the extent permitted by applicable law and AAA rules:</p>
          <ul className="list-disc pl-5 space-y-1 mt-1">
            <li>
              you agree to pay any required consumer filing fee and your own attorney's fees and
              costs; and
            </li>
            <li>the arbitrator may award fees and costs as permitted by law.</li>
          </ul>
          <p className="mt-2">
            If AAA rules require Grunt Logic to pay certain fees for the arbitration to proceed,
            Grunt Logic will pay those required amounts.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">E. Class Action Waiver</p>
          <p>
            You and Grunt Logic agree that disputes will be brought only in an individual
            capacity and not as a plaintiff or class member in any purported class, collective,
            consolidated, or representative action.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">F. Exceptions</p>
          <p>
            Either party may bring an individual claim in small claims court if it qualifies.
            Either party may seek injunctive relief in court to stop unauthorized use, abuse, or
            infringement relating to the Service, intellectual property, or confidential
            information.
          </p>
        </div>
        <div>
          <p className="font-medium text-foreground mb-1">G. Opt-Out of Arbitration (How It Works)</p>
          <p>
            You may opt out of the arbitration and class action waiver provisions in this Section
            22 by emailing{' '}
            <a href="mailto:support@firecaseiq.com" className="text-primary hover:underline">
              support@firecaseiq.com
            </a>{' '}
            within 30 days of first accepting these Terms.
          </p>
          <p className="mt-2">Email from your account email address with:</p>
          <ul className="list-disc pl-5 space-y-1 mt-1">
            <li>Subject: "ARBITRATION OPT-OUT"</li>
            <li>Your full legal name</li>
            <li>Your account email</li>
            <li>Your mailing address</li>
            <li>A clear statement that you are opting out of arbitration and the class action waiver</li>
          </ul>
          <p className="mt-2">
            If you opt out, disputes between you and Grunt Logic will be resolved in court under
            Section 23.
          </p>
        </div>
      </div>
    ),
  },
  {
    number: '23',
    title: 'Governing Law; Venue (North Carolina)',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          These Terms are governed by the laws of the State of North Carolina, without regard to
          conflict-of-law rules. To the extent any dispute is permitted in court, you agree to
          exclusive venue in the state or federal courts located in Wake County, North Carolina,
          and you consent to personal jurisdiction there.
        </p>
      </div>
    ),
  },
  {
    number: '24',
    title: 'Contact',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <div className="grid gap-2">
          <div className="flex flex-col sm:flex-row sm:gap-2">
            <span className="font-medium text-foreground min-w-[200px]">General / Legal / Support:</span>
            <a href="mailto:info@firecaseiq.com" className="text-primary hover:underline">
              info@firecaseiq.com
            </a>
          </div>
          <div className="flex flex-col sm:flex-row sm:gap-2">
            <span className="font-medium text-foreground min-w-[200px]">Support Notices (Section 22):</span>
            <a href="mailto:support@firecaseiq.com" className="text-primary hover:underline">
              support@firecaseiq.com
            </a>
          </div>
          <div className="flex flex-col sm:flex-row sm:gap-2">
            <span className="font-medium text-foreground min-w-[200px]">Marketplace Disputes (Section 14):</span>
            <a href="mailto:disputes@firecaseiq.com" className="text-primary hover:underline">
              disputes@firecaseiq.com
            </a>
          </div>
          <div className="flex flex-col sm:flex-row sm:gap-2">
            <span className="font-medium text-foreground min-w-[200px]">Enterprise / Sales:</span>
            <a href="mailto:sales@firecaseiq.com" className="text-primary hover:underline">
              sales@firecaseiq.com
            </a>
          </div>
        </div>
        <div className="pt-2 border-t border-border mt-3">
          <p className="font-medium text-foreground">Filmology LLC d/b/a Grunt Logic</p>
          <p>5448 Apex Peakway Suite 157</p>
          <p>Apex, NC 27502</p>
        </div>
      </div>
    ),
  },
];

const AccordionItem = ({ section, isOpen, onToggle }) => (
  <div className="border border-border rounded-lg overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-5 py-4 bg-card hover:bg-muted/50 transition-colors text-left group"
    >
      <div className="flex items-center gap-3">
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
          {section.number}
        </span>
        <span className="font-semibold text-foreground text-sm">{section.title}</span>
      </div>
      <Icon
        name={isOpen ? 'ChevronUp' : 'ChevronDown'}
        size={18}
        className="text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0 ml-3"
      />
    </button>
    {isOpen && (
      <div className="px-5 py-4 bg-background border-t border-border">
        {section.content}
      </div>
    )}
  </div>
);

const TermsOfServicePage = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center">
            <img src="/assets/images/logo.png" alt="FireCaseIQ" className="h-10 object-contain" />
          </button>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="ArrowLeft" size={20} />
            Back
          </button>
        </div>
      </header>

      {/* Page content */}
      <main className="max-w-3xl mx-auto px-4 md:px-6 py-10 space-y-8">
        {/* Title block */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">Terms of Service</h1>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium">Effective Date:</span> February 15, 2026
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <span className="font-medium">Last Updated:</span> February 27, 2026
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed pt-1">
            These Terms of Service ("Terms") are a binding agreement between you and Filmology LLC
            d/b/a Grunt Logic ("Grunt Logic," "we," "us," or "our") governing your access to and
            use of FireCaseIQ (the "Service"). By using the Service, you agree to these Terms. If
            you do not agree, do not use the Service.
          </p>
        </div>

        {/* Accordion sections */}
        <div className="space-y-3">
          {sections.map((section, i) => (
            <AccordionItem
              key={section.number}
              section={section}
              isOpen={openIndex === i}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

export default TermsOfServicePage;
